import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, MapPin, Building2, User, Printer } from 'lucide-react';
import { customerDetails } from '../lib/customer';
import { printInvoice, printDeliveryNote, printOrder, taxLabel, isTaxed } from '../lib/documents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const money = (value) => `Tsh ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const day = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const STATUS_COLOURS = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  approved: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  delivered: 'bg-green-100 text-green-800',
  paid: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  unpaid: 'bg-slate-100 text-slate-700',
  cancelled: 'bg-red-100 text-red-800',
  rejected: 'bg-red-100 text-red-800',
};

const StatusBadge = ({ status }) => (
  status ? (
    <Badge variant="outline" className={STATUS_COLOURS[status] || 'bg-slate-100 text-slate-700'}>{status}</Badge>
  ) : <span className="text-slate-400">—</span>
);

const Stat = ({ label, value, hint }) => (
  <Card>
    <CardContent className="pt-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </CardContent>
  </Card>
);

const Detail = ({ label, children }) => (
  <div>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-sm text-slate-900">{children || <span className="text-slate-400">—</span>}</p>
  </div>
);

// Everything the business holds on one customer, in the order staff ask for it:
// who they are, what they are worth, then the documents themselves.
export default function CustomerProfile() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // The document opened from one of the tabs: { kind, doc }
  const [viewing, setViewing] = useState(null);
  // Needed only to print an invoice raised before the payment register existed
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [defaultTerms, setDefaultTerms] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/customers/${customerId}/profile`, authHeaders());
        if (!cancelled) setProfile(res.data);
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.detail || 'Failed to load customer profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const loadPrintContext = async () => {
      try {
        const [accounts, defaults] = await Promise.all([
          axios.get(`${API}/payment-accounts`, authHeaders()),
          axios.get(`${API}/settings/invoice-defaults`, authHeaders()),
        ]);
        if (cancelled) return;
        setPaymentAccounts((accounts.data || []).filter(a => a.is_active !== false));
        setDefaultTerms(defaults.data?.payment_terms || '');
      } catch (error) {
        console.error('Failed to load print details');
      }
    };
    loadPrintContext();
    return () => { cancelled = true; };
  }, [customerId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-10" data-testid="customer-profile-loading">
        <Loader2 size={16} className="animate-spin" /> Loading customer…
      </div>
    );
  }

  if (!profile) {
    return (
      <div data-testid="customer-profile-missing">
        <Button variant="ghost" onClick={() => navigate('/customers')}>
          <ArrowLeft size={16} className="mr-2" /> Back to customers
        </Button>
        <p className="text-slate-600 mt-4">This customer could not be loaded.</p>
      </div>
    );
  }

  const { customer, source, orders, portal_orders: portalOrders, invoices, delivery_notes: notes, summary } = profile;
  const details = customerDetails(customer);
  const phones = (customer.phone_numbers?.length ? customer.phone_numbers : [customer.phone]).filter(Boolean);
  // Staff-raised and portal orders are one history to the person reading it
  const allOrders = [
    ...orders.map((o) => ({ ...o, origin: 'Internal' })),
    ...portalOrders.map((o) => ({ ...o, origin: 'Portal' })),
  ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <div data-testid="customer-profile-page">
      <Button variant="ghost" className="mb-4 -ml-2" onClick={() => navigate('/customers')} data-testid="back-to-customers">
        <ArrowLeft size={16} className="mr-2" /> Back to customers
      </Button>

      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            {details.registeredAsCompany ? <Building2 size={22} className="text-blue-600" /> : <User size={22} className="text-slate-500" />}
            <h1 className="text-3xl font-bold text-slate-900">{customer.name || '(no name)'}</h1>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className={details.registeredAsCompany ? 'bg-blue-50 text-blue-700' : 'bg-slate-50'}>
              {details.entityLabel || 'Customer'}
            </Badge>
            <Badge variant="outline" className="bg-slate-50 text-slate-600">
              {source === 'portal' ? 'Portal account' : 'Customer register'}
            </Badge>
            <span className="text-xs text-slate-500">
              Last activity {day(summary.last_activity_at)}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Orders" value={summary.orders_count} hint={`${money(summary.total_ordered)} ordered`} />
        <Stat label="Invoices" value={summary.invoices_count}
          hint={summary.proformas_count ? `${summary.proformas_count} proforma(s) besides` : 'Official invoices'} />
        <Stat label="Total invoiced" value={money(summary.total_invoiced)} hint="Proformas excluded" />
        <Stat label="Outstanding" value={money(summary.balance_due)} hint={`${money(summary.total_paid)} paid`} />
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Customer details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {details.registeredAsCompany
              ? <Detail label="Contact person">{customer.contact_person}</Detail>
              : <Detail label="Organisation">{details.companyName !== customer.name ? details.companyName : ''}</Detail>}
            <Detail label="TIN number">{customer.tin_number}</Detail>
            <Detail label="Phone">{phones.join(', ')}</Detail>
            <Detail label="Email">{customer.email}</Detail>
            <Detail label="Address">
              {customer.location?.lat ? (
                <a className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  href={`https://www.google.com/maps?q=${customer.location.lat},${customer.location.lon}`}
                  target="_blank" rel="noreferrer">
                  <MapPin size={14} />{customer.address || customer.location.address}
                </a>
              ) : customer.address}
            </Detail>
            <Detail label="Onboarded by">{customer.onboarded_by_name}</Detail>
            <Detail label="Customer since">{day(customer.created_at || summary.first_activity_at)}</Detail>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="orders">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger value="orders">Orders ({allOrders.length})</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="deliveries">Deliveries ({notes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Raised</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allOrders.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-500">No orders yet.</TableCell></TableRow>
                ) : allOrders.map((order) => (
                  <TableRow key={order.id} data-testid={`profile-order-${order.id}`}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setViewing({ kind: 'order', doc: order })}>
                    <TableCell className="font-medium">{order.order_number}</TableCell>
                    <TableCell className="text-sm text-slate-600">{order.origin}</TableCell>
                    <TableCell>{day(order.created_at)}</TableCell>
                    <TableCell>{(order.items || []).length}</TableCell>
                    <TableCell>{money(order.total_amount)}</TableCell>
                    <TableCell><StatusBadge status={order.status} /></TableCell>
                    <TableCell>
                      {order.origin === 'Portal'
                        ? <StatusBadge status={order.paid_status} />
                        : <span className="text-slate-400">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="invoices">
          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Subtotal</TableHead>
                  <TableHead>Tax</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-500">No invoices yet.</TableCell></TableRow>
                ) : invoices.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`profile-invoice-${invoice.id}`}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setViewing({ kind: 'invoice', doc: invoice })}>
                    <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={invoice.invoice_type === 'proforma' ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'}>
                        {invoice.invoice_type === 'proforma' ? 'Proforma' : 'Invoice'}
                      </Badge>
                    </TableCell>
                    <TableCell>{day(invoice.created_at)}</TableCell>
                    <TableCell>{money(invoice.subtotal)}</TableCell>
                    <TableCell>
                      {invoice.tax_rate ? `${invoice.tax_name || 'Tax'} ${invoice.tax_rate}%` : 'None'}
                      <span className="text-slate-500"> · {money(invoice.tax_amount)}</span>
                    </TableCell>
                    <TableCell className="font-medium">{money(invoice.total_amount)}</TableCell>
                    <TableCell>
                      {invoice.invoice_type === 'proforma'
                        ? <StatusBadge status={invoice.validation_status || 'pending'} />
                        : <span className="text-slate-400">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="deliveries">
          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Note</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Delivery address</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Vehicle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notes.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500">No deliveries yet.</TableCell></TableRow>
                ) : notes.map((note) => (
                  <TableRow key={note.id} data-testid={`profile-note-${note.id}`}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setViewing({ kind: 'note', doc: note })}>
                    <TableCell className="font-medium">{note.delivery_number}</TableCell>
                    <TableCell>{day(note.delivery_date || note.created_at)}</TableCell>
                    <TableCell>{(note.items || []).length}</TableCell>
                    <TableCell className="text-sm">{note.delivery_address || '—'}</TableCell>
                    <TableCell className="text-sm">{note.driver_name || '—'}</TableCell>
                    <TableCell className="text-sm">{note.vehicle_number || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!viewing} onOpenChange={(next) => !next && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="document-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {viewing.kind === 'order' && `Order ${viewing.doc.order_number}`}
                  {viewing.kind === 'invoice' && `${viewing.doc.invoice_type === 'proforma' ? 'Proforma' : 'Invoice'} ${viewing.doc.invoice_number}`}
                  {viewing.kind === 'note' && `Delivery note ${viewing.doc.delivery_number}`}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Detail label="Date">{day(viewing.doc.delivery_date || viewing.doc.created_at)}</Detail>
                  <Detail label="Customer">{customer.name}</Detail>
                  {viewing.kind === 'order' && (
                    <>
                      <Detail label="Status">{viewing.doc.status}</Detail>
                      <Detail label="Delivery address">{viewing.doc.delivery_address}</Detail>
                      <Detail label="Paid by">
                        {viewing.doc.payment_account?.label || viewing.doc.payment_method}
                      </Detail>
                      <Detail label="Payment reference">{viewing.doc.payment_reference}</Detail>
                    </>
                  )}
                  {viewing.kind === 'invoice' && (
                    <>
                      <Detail label="Tax">
                        {isTaxed(viewing.doc) ? taxLabel(viewing.doc) : 'Not charged'}
                      </Detail>
                      <Detail label="Payment terms">{viewing.doc.payment_terms || defaultTerms}</Detail>
                      <Detail label="Pay into">
                        {(viewing.doc.payment_accounts || []).map(a => a.label).join(', ')}
                      </Detail>
                    </>
                  )}
                  {viewing.kind === 'note' && (
                    <>
                      <Detail label="Delivery address">{viewing.doc.delivery_address}</Detail>
                      <Detail label="Driver">{viewing.doc.driver_name}</Detail>
                      <Detail label="Vehicle">{viewing.doc.vehicle_number}</Detail>
                    </>
                  )}
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Quantity</TableHead>
                        {viewing.kind !== 'note' && <TableHead>Unit price</TableHead>}
                        {viewing.kind !== 'note' && <TableHead className="text-right">Amount</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(viewing.doc.items || []).map((item, index) => {
                        const quantity = Number(item.quantity || 0);
                        const unit = Number(item.unit_price || 0);
                        return (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell>{quantity}</TableCell>
                            {viewing.kind !== 'note' && <TableCell>{money(unit)}</TableCell>}
                            {viewing.kind !== 'note' && (
                              <TableCell className="text-right">
                                {money(item.amount ?? item.total_price ?? quantity * unit)}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {viewing.kind !== 'note' && (
                  <div className="border-t pt-3 space-y-1 text-sm">
                    {viewing.kind === 'invoice' && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Subtotal</span>
                        <span>{money(viewing.doc.subtotal)}</span>
                      </div>
                    )}
                    {isTaxed(viewing.doc) && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">{taxLabel(viewing.doc)}</span>
                        <span>{money(viewing.doc.tax_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-base">
                      <span>Total</span>
                      <span>{money(viewing.doc.total_amount)}</span>
                    </div>
                  </div>
                )}

                {viewing.doc.notes && <p className="text-sm text-slate-600">{viewing.doc.notes}</p>}

                <Button
                  className="w-full"
                  data-testid="print-document-button"
                  onClick={() => {
                    if (viewing.kind === 'invoice') {
                      printInvoice(viewing.doc, { customers: [customer], paymentAccounts, defaultTerms });
                    } else if (viewing.kind === 'note') {
                      printDeliveryNote(viewing.doc, { customers: [customer] });
                    } else {
                      printOrder(viewing.doc, { customer });
                    }
                  }}
                >
                  <Printer size={16} className="mr-2" />
                  Print {viewing.kind === 'invoice' ? 'invoice' : viewing.kind === 'note' ? 'delivery note' : 'order'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
